import { FolderOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Modal, Select, Tag } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormItem from '@/components/form/FormItem';
import { MODAL_WIDTH_SM, SPACE_MD, SPACE_SM } from '@/theme/buildAntdTheme';
import ModalFooter from '@/UI/ModalFooter';
import { LabelTitle, Meta } from '@/UI/typography';
import { searchShowSearch } from '@/utils/selectHelpers';

import { useSwitchUserPopup } from './hooks';

const NEAR_BOTTOM_PX = 100;

interface Iprops {
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
}

const SwitchUserPopup = ({ isVisible, setVisible }: Iprops) => {
  const [t] = useTranslation();

  const {
    actions: {
      handleSearchClientProfile,
      handleSearchRecruiter,
      handleSelectClientProfile,
      handleSelectRecruiter,
      handleConfirm,
      handleBackToAdminRole,
      loadMoreClientProfiles,
      loadMoreRecruiters,
    },
    data: {
      clientProfiles,
      recruiters,
      selectedClientProfileId,
      selectedRecruiterId,
      loading,
      canSwitchUsers,
      isImpersonating,
      getRemoteRoleLabel,
    },
  } = useSwitchUserPopup(isVisible);

  const handleClose = () => setVisible(false);

  // The dropdown scroller is rc-virtual-list's internal holder, so there is no
  // ref to hand useLoadMoreOnScroll — run the same near-bottom check here.
  const loadMoreNearBottom = useCallback(
    (event: React.UIEvent<HTMLDivElement>, loadMore: () => void) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX) {
        loadMore();
      }
    },
    [],
  );

  const handleClientProfileScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) =>
      loadMoreNearBottom(event, loadMoreClientProfiles),
    [loadMoreNearBottom, loadMoreClientProfiles],
  );

  const handleRecruiterScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) =>
      loadMoreNearBottom(event, loadMoreRecruiters),
    [loadMoreNearBottom, loadMoreRecruiters],
  );

  const isProfileAdmin = useCallback(
    (profile: (typeof clientProfiles)[number]) => {
      if (profile.is_remote_admin) return true;
      const userType = profile.remote_user_type?.toUpperCase();
      return !!userType && userType === 'ADMIN';
    },
    [],
  );

  const clientProfileOptions = useMemo(() => {
    const groupedProfiles = clientProfiles.reduce(
      (acc, profile) => {
        const companyName = profile.company.name;
        if (!acc[companyName]) {
          acc[companyName] = [];
        }
        acc[companyName].push(profile);
        return acc;
      },
      {} as Record<string, typeof clientProfiles>,
    );

    const options: Array<{
      value: string;
      label: React.ReactNode;
      disabled?: boolean;
    }> = [];

    Object.entries(groupedProfiles).forEach(([companyName, profiles]) => {
      profiles.sort((a, b) => {
        const adminDiff = Number(isProfileAdmin(b)) - Number(isProfileAdmin(a));
        if (adminDiff) return adminDiff;
        return a.name.localeCompare(b.name);
      });

      if (profiles.length === 1) {
        const profile = profiles[0];
        options.push({
          value: profile.id,
          label: (
            <Flex align="center" justify="space-between">
              <Flex align="center" wrap>
                <FolderOutlined style={{ color: 'var(--ant-color-warning)' }} />
                <LabelTitle>{companyName}</LabelTitle>
                <Meta type="secondary">{profile.name}</Meta>
              </Flex>
              {isProfileAdmin(profile) && (
                <Tag color="warning">{t('ADMIN')}</Tag>
              )}
            </Flex>
          ),
        });
      } else {
        options.push({
          value: `header-${companyName}`,
          disabled: true,
          label: (
            <Flex align="center">
              <FolderOutlined style={{ color: 'var(--ant-color-warning)' }} />
              <LabelTitle>
                {companyName} ({profiles.length})
              </LabelTitle>
            </Flex>
          ),
        });

        profiles.forEach((profile) => {
          const roleLabel = getRemoteRoleLabel(profile);
          options.push({
            value: profile.id,
            label: (
              <Flex vertical style={{ padding: `${SPACE_SM}px ${SPACE_MD}px` }}>
                <Flex align="center" justify="space-between">
                  <Flex align="center" wrap>
                    <UserOutlined />
                    <LabelTitle>{profile.name}</LabelTitle>
                    {profile.department && <Meta>{profile.department}</Meta>}
                  </Flex>
                  {isProfileAdmin(profile) && (
                    <Tag color="warning">{t('ADMIN')}</Tag>
                  )}
                </Flex>
                {roleLabel && <Meta ellipsis>{roleLabel}</Meta>}
              </Flex>
            ),
          });
        });
      }
    });

    return options;
  }, [clientProfiles, getRemoteRoleLabel, isProfileAdmin, t]);

  const recruiterOptions = useMemo(
    () =>
      recruiters.map((rec) => ({
        value: rec.id,
        label: (
          <Flex vertical>
            <Flex align="center" justify="space-between">
              <Flex align="center" wrap>
                <UserOutlined />
                <LabelTitle>{rec.name}</LabelTitle>
                {rec.company?.name !== rec.name && (
                  <Meta>{rec.company?.name}</Meta>
                )}
              </Flex>
              {rec.recruiter_type && (
                <Tag>{rec.recruiter_type.toUpperCase()}</Tag>
              )}
            </Flex>
          </Flex>
        ),
      })),
    [recruiters],
  );

  return (
    <Modal
      open={isVisible}
      title={t('SWITCH_ROLE')}
      onOk={handleConfirm}
      onCancel={handleClose}
      width={MODAL_WIDTH_SM}
      footer={
        <ModalFooter
          actions={
            <>
              <Button color="primary" variant="outlined" onClick={handleClose}>
                {t('CANCEL')}
              </Button>
              {isImpersonating && (
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleBackToAdminRole}
                  loading={loading}
                >
                  {t('BACK_TO_ADMIN_ROLE')}
                </Button>
              )}
              <Button type="primary" onClick={handleConfirm} loading={loading}>
                {t('SELECT_ROLE')}
              </Button>
            </>
          }
        />
      }
    >
      <>
        {canSwitchUsers && (
          <Form layout="vertical">
            <FormItem label={t('CLIENTS_SELECT_LABEL')}>
              <Select
                defaultActiveFirstOption={false}
                showSearch={searchShowSearch(handleSearchClientProfile)}
                notFoundContent={null}
                onSelect={handleSelectClientProfile}
                onPopupScroll={handleClientProfileScroll}
                value={selectedClientProfileId}
                placeholder={t('SELECT_CLIENT_PROFILE_PLACEHOLDER')}
                options={clientProfileOptions}
              />
            </FormItem>

            <FormItem label={t('RECRUITERS_SELECT_LABEL')}>
              <Select
                defaultActiveFirstOption={false}
                showSearch={searchShowSearch(handleSearchRecruiter)}
                onSelect={handleSelectRecruiter}
                onPopupScroll={handleRecruiterScroll}
                value={selectedRecruiterId}
                options={recruiterOptions}
              />
            </FormItem>
          </Form>
        )}
      </>
    </Modal>
  );
};

export default SwitchUserPopup;
